/**
 * 3x3 matrix functions for rotation, translation, scaling, etc. A matrix is
 * presumed to be an array containing 9 elements, which would be mapped to a 3x3
 * matrix thus:
 *
 * | [0] [1] [2] |
 * | [3] [4] [5] |
 * | [6] [7] [8] |
 *
 * In some special cases, a 6 element matrix is acceptable.
 */
export default class MatrixUtil {
    /**
     * create a new identity matrix instance, or set the matrix given to the identity matrix
     * 
     * @param matrix (Optional) matrix to set to the identity
     * 
     * @returns a new identity matrix instance
     */
    identity(matrix?: number[]): number[] {
        if (matrix) {
            for(let i = 0; i < 9; i++) {
                matrix[i] = (i%4) ? 0 : 1;
            }
            matrix.length = 9;
            return matrix;
        } else {
            return [1,0,0,0,1,0,0,0,1];
        }
    }
    
    /**
     * Determine if the matrix given is an identity matrix
     * 
     * @param matrix matrix to test
     * 
     * @returns true if the matrix given is an identity matrix, false otherwise
     */
    isIdentity(matrix: number[]): boolean {
        return this.equal(matrix, [1,0,0,0,1,0,0,0,1]);
    }
    
    /**
     * convert the given 6 element matrix to a 9 element matrix. As part of the process, the y axis is inverted. (because the 6 element matrices
     * used by canvas wor this way)
     * 
     * @param matrix an array containing 6 elements
     * 
     * @returns an array containing 9 elements representing a 3x3 matrix, or null if the parameter could not be converted
     */
    to9Elements(matrix: number[]): number[] | null {
        if (matrix.length == 9) {
            return matrix;
        } else if(matrix.length == 6) {
            return [matrix[0], -matrix[1], matrix[4], -matrix[2], matrix[3], matrix[5], 0, 0, 1];
        } else {
            return null;
        }
    }
    
    /**
     * convert the given 9 element matrix to a 6 element matrix. As part of the process, the y axis is inverted. (because the 6 element matrices
     * used by canvas wor this way)
     * 
     * @param matrix an array containing 9 elements
     * 
     * @returns an array containing 6 elements representing a 3x3 matrix, or null if the parameter could not be converted
     */
    to6Elements(matrix: number[]): number[] | null {
        if (matrix.length == 6) {
            return matrix;
        } else if(matrix.length == 9) {
            return [matrix[0], -matrix[1], -matrix[3], matrix[4], matrix[2], matrix[5]];
        } else {
            return null;
        }
    }
    
    /**
     * Get the product of the matrices given and return it as a new matrix. - the order of the operation
     * is "a then b" (This may be slightly confusing to those with experience using matrices who know that
     * F(ab) is "b then a", but it will probably result in less errors from a purely API perspective.
     * 
     * @param m1 a matrix
     * @param m2 a matrix
     * 
     * @returns a 9 element array representing the product of all given matri
     */
    product(m1: number[], m2: number[]) {
        const a = this.to9Elements(m2) as number[];
        const b = this.to9Elements(m1) as number[];
        
        const ret = [];
        ret[0] = (a[0] * b[0]) + (a[1] * b[3]) + (a[2] * b[6]);
        ret[1] = (a[0] * b[1]) + (a[1] * b[4]) + (a[2] * b[7]);
        ret[2] = (a[0] * b[2]) + (a[1] * b[5]) + (a[2] * b[8]);
        
        ret[3] = (a[3] * b[0]) + (a[4] * b[3]) + (a[5] * b[6]);
        ret[4] = (a[3] * b[1]) + (a[4] * b[4]) + (a[5] * b[7]);
        ret[5] = (a[3] * b[2]) + (a[4] * b[5]) + (a[5] * b[8]);
        
        ret[6] = (a[6] * b[0]) + (a[7] * b[3]) + (a[8] * b[6]);
        ret[7] = (a[6] * b[1]) + (a[7] * b[4]) + (a[8] * b[7]);
        ret[8] = (a[6] * b[2]) + (a[7] * b[5]) + (a[8] * b[8]);
        
        return ret;
    }
    
    /**
     * Get the product of the matrix given and a new rotation matrix derived from the angle given (measured in radians)
     * if no matrix or point are defined simply return the rotation matrix:
     * | cos,sin,  0|
     * |-sin,cos,  0|
     * |   0,  0,  1|
     * If a point is defined, then rotation should occur around this point
     * 
     * @param radians the angle of rotation in radians
     * @param point point around which to rotate (defaults to 0,0)
     * @param matrix (Optional) matrix to which the rotation should be appended
     * 
     * @returns a new matrix containing the rotation given
     */
    rotate(radians: number, point: number[], matrix: number[]): number[] {
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const rot = [cos,-sin,0, sin,cos,0, 0,0,1];

        let ret;
        if (matrix || point) {
            ret = matrix;
            if (point) {
                ret = this.translate([-point[0], -point[1]], ret);
            }
            ret = this.product(ret, rot);
            if (point) {
                ret = this.translate(point, ret);
            }
        } else {
            ret = rot;
        }

        return ret;
    }
    
    /**
     * Get the product of the matrix given and a new rotation matrix derived from the angle given (measured in degrees)
     * if no matrix or point are defined simply return the rotation matrix:
     * | cos,sin,  0|
     * |-sin,cos,  0|
     * |   0,  0,  1|
     * If a point is defined, then rotation should occur around this point
     * 
     * @param degrees the angle of rotation in degrees
     * @param point point around which to rotate (defaults to 0,0)
     * @param matrix (Optional) matrix to which the rotation should be appended
     * 
     * @returns a new matrix containing the rotation given
     */
    rotateDegrees(degrees: number, point: number[], matrix: number[]): number[] {
        return this.rotate((degrees*Math.PI/180), point, matrix);
    }
    
    /**
     * Get the product of the matrix given and a new translation matrix derived from the vector given.
     * if no matrix was defined, simply return the translation matrix.
     * [1, 0, x]
     * [0, 1, y]
     * [0, 0, 1]
     * 
     * @param vector translation vector
     * @param matrix (Optional) matrix to which the translation should be applied
     * 
     * @returns a new matrix containing a translation by the vector given
     */
    translate(vector: number[], matrix?: number[]): number[] {
        let ret = [1,0,vector[0], 0,1,vector[1], 0,0,1];
        if (matrix) {
            ret = this.product(matrix, ret);
        }

        return ret;
    }
    
    /**
     * Get the product of the matrix given and a new matrix which will flip the y axis. This is useful for translating between canvas space
     * and projected space
     * [1,  0, 0]
     * [0, -1, 0]
     * [0,  0, 1]
     * 
     * @param matrix (Optional) matrix to which the translation should be applied
     * 
     * @returns a new matrix containing a translation by the vector given
     */
    flipY(matrix?: number[]): number[] {
        let ret = [1,0,0, 0,-1,0, 0,0,1];
        if (matrix) {
            ret = this.product(matrix, ret);
        }

        return ret;
    }
    
    /**
     * Get the product of the matrix given and a new scaling matrix derived from the vector/number given.
     * if no matrix or scale point was defined, simply return the scaling matrix.
     * [x, 0, 0]
     * [0, y, 0]
     * [0, 0, 1]
     * 
     * @param vector scaling vector (if it is a number, then it is translated to [vector,vector] to apply equal scale to each axis)
     * @param point point around which to scale - defaults to 0,0
     * @param matrix (Optional) matrix to which the scale should be applied
     * 
     * @returns a new matrix containing a scale by the vector/number given
     */
    scale(vector: number | number[], point: number[], matrix?: number[]): number[] {
        let scale;
        if (typeof vector == "number") {
            scale = [vector,0,0, 0,vector,0, 0,0,1];
        } else {
            scale = [vector[0],0,0, 0,vector[1],0, 0,0,1];
        }

        let ret;
        if (matrix || point) {
            ret = matrix;
            if (point) {
                ret = this.translate([-point[0],-point[1]], ret);
            }

            ret = this.product(ret, scale);

            if (point) {
                ret = this.translate(point, ret);
            }
        } else {
            ret = scale;
        }

        return ret;
    }
    
    /**
     * Calculate and return the inverse of the matrix given. NOTE: Due to rounding errors with floating point numbers, the matrix
     * returned is not always the exact inverse of the original matrix, but pretty close.
     * 
     * @param m the matrix for which an inverse is required
     * 
     * @returns a new matrix such that EMatrix.product(m, newMatrix) == [1,0,0,0,1,0,0,0,1] (The identity matrix)
     */
    inverse(m: number[]): number[] {
        const det = (m[0] * ((m[8] * m[4]) - (m[7] * m[5]))) - (m[3] * ((m[8] * m[1]) - (m[7] * m[2]))) + (m[6] * ((m[5] * m[1]) - (m[4] * m[2])));
        const mul = 1 / det;
        
        const ret = [];
        ret[0] =  mul * ((m[8] * m[4]) - (m[7] * m[5]));
        ret[1] = -mul * ((m[8] * m[1]) - (m[7] * m[2]));
        ret[2] =  mul * ((m[5] * m[1]) - (m[4] * m[2]));
        ret[3] = -mul * ((m[8] * m[3]) - (m[6] * m[5]));
        ret[4] =  mul * ((m[8] * m[0]) - (m[6] * m[2]));
        ret[5] = -mul * ((m[5] * m[0]) - (m[3] * m[2]));
        ret[6] =  mul * ((m[7] * m[3]) - (m[6] * m[4]));
        ret[7] = -mul * ((m[7] * m[0]) - (m[6] * m[1]));
        ret[8] =  mul * ((m[4] * m[0]) - (m[3] * m[1]));

        return ret;
    }
    
    /**
     * Transform the vector given using the matrix given.
     * 
     * @param matrix the matrix to use to transform the vector
     * @param vectors, a vector or vectors to be transformed - an array of numbers representing 2d points
     * 
     * @returns a new vector containing the result of the transformation
     */
    transform(matrix: number[], vectors: number[], startIndex?: number, endIndex?: number, result?: any[]): any[] {
        let ords;
        let i = 0;
        let end;
        let ret: number[];
        if (typeof vectors === "number") {
            ords = arguments;
            i++;
            end = arguments.length;
            ret = [];
        } else {
            ords = vectors;
            i = startIndex ? startIndex : 0;
            end = endIndex ? endIndex : vectors.length;
            ret = result ? result : [];
        }

        while (i < end) {
            const x = ords[i++];
            const y = ords[i++];
            const dx = (matrix[0]*x) + (matrix[1] * y) + matrix[2];
            const dy = (matrix[3]*x) + (matrix[4] * y) + matrix[5];

            ret.push(dx, dy);
            //ret.push( ((dx * 10) << 0) * 0.1, ((dy * 10) << 0) * 0.1);
            //ret.push( Math.round(dx), Math.round(dy) );
        }

        return ret;
    }
    
    /**
     * Compare the matrices given.
     * @param m1 first matrix
     * @param m2 second matrix
     * @param epsilon - amount of inaccuracy that is tolerated. (required because numbers are floating point)
     * @return true if matrices match, false otherwise
     */
    equal(m1: number[], m2: number[], epsilon?: number): boolean {
        let len = arguments.length;
        let e = epsilon;
        if (typeof e == "number") {
            len--;
        } else {
            e = 0.00001;
        }

        const epsilonSq = epsilon * epsilon;
        function numberMatch(a: number, b: number): boolean {
            const absA = Math.abs(a);
            const absB = Math.abs(b);
            const diff = Math.abs(a - b);
            if (a == b) {
                return true; // shortcut, handles infinities
            } else if (a * b == 0) { // a or b or both are zero relative error is not meaningful here
                return diff < epsilonSq;
            } else { // use relative error
                return diff / (absA + absB) < epsilon;
            }
        }
        
        for (let i = 0; i < len; i++) {
            if (!arguments[i].length) {
                return false; // not a matrix
            }

            if (arguments[i].length != 9) {
                arguments[i] = this.to9Elements(arguments[i]);
            }
        }

        for (let j = 1; j < len; j++) {
            for(let k = 0; k < 9; k++) {
                if (numberMatch(arguments[j-1][k], arguments[j][k])) {
                    return false; // mismatch
                }
            }
        }

        return true;
    }
}
